import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useVenueId } from '../lib/session.js';
import { useVenue } from '../hooks/useVenue.js';
import { currentServiceDate, formatDate, formatHijri } from '../lib/time.js';
import { ServiceDatePicker } from '../components/ServiceDatePicker.js';

interface PreShift {
  service_date: string;
  totals: { bookings: number; covers: number };
  allergies: Array<{ booking_ref: string; clock: string; guest: string; allergies: string; dietary: string[] }>;
  vips: Array<{ booking_ref: string; clock: string; guest: string; vip_level: number; total_visits: number; tags: string[] }>;
  occasions: Array<{ booking_ref: string; clock: string; guest: string; occasion: string }>;
  large_parties: Array<{ booking_ref: string; clock: string; guest: string; party_size: number }>;
  special_requests: Array<{ booking_ref: string; clock: string; request: string }>;
  covers_by_interval: Array<{ clock: string; covers: number; parties: number }>;
  peak_intervals: Array<{ clock: string; covers: number; parties: number }>;
  bookings: Array<{
    booking_ref: string;
    clock: string;
    guest: string;
    phone: string | null;
    party_size: number;
    status: string;
    visits: number;
  }>;
}

/**
 * The pre-shift briefing.
 *
 * Printed and read aloud before doors, so it is ordered by what will bite first
 * — allergies, then VIPs, then occasions, then large parties — rather than by
 * time. Covers by interval come last: they inform the kitchen, not the door.
 *
 * The print stylesheet drops the chrome and keeps the direction, so an Arabic
 * briefing prints right-to-left rather than as mirrored English.
 */
export function PreShiftScreen() {
  const { t, i18n } = useTranslation();
  const venueId = useVenueId();
  const venue = useVenue();

  const [serviceDate, setServiceDate] = useState(() =>
    venue ? currentServiceDate(venue) : '',
  );
  const date = serviceDate || (venue ? currentServiceDate(venue) : '');

  const report = useQuery({
    queryKey: ['pre-shift', venueId, date],
    enabled: Boolean(venueId && date),
    queryFn: () => api.get<PreShift>('/api/v1/pre-shift', { venue_id: venueId, date }),
  });

  if (!venue) return <p className="text-sm text-slate-500">{t('app.loading')}</p>;

  const data = report.data;
  const maxCovers = Math.max(1, ...(data?.covers_by_interval ?? []).map((row) => row.covers));

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-end justify-between gap-3 print:block">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold">{t('preShift.title')}</h1>
          <div className="print:hidden">
            <ServiceDatePicker value={date} onChange={setServiceDate} venue={venue} />
          </div>
          {/* Both calendars on the printed page: during Ramadan the briefing is
              given by the Hijri date. */}
          <p className="hidden text-sm print:block">
            {formatDate(date, venue, i18n.language)} · {formatHijri(date, venue, i18n.language)}
          </p>
        </div>

        <button
          type="button"
          onClick={() => window.print()}
          className="rounded-md border border-slate-200 px-3 py-1.5 text-sm print:hidden"
        >
          {t('preShift.print')}
        </button>
      </header>

      {data ? (
        <>
          <section className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              ['preShift.bookings', data.totals.bookings],
              ['floor.covers', data.totals.covers],
              ['preShift.allergies', data.allergies.length],
              ['preShift.vips', data.vips.length],
            ].map(([key, value]) => (
              <div key={String(key)} className="rounded-lg border border-slate-200 bg-white p-3">
                <div className="text-xs uppercase tracking-wide text-slate-500">
                  {t(String(key))}
                </div>
                <div className="font-mono text-2xl" data-numeric>
                  {String(value)}
                </div>
              </div>
            ))}
          </section>

          {/* Allergies lead. A briefing that buries them below a covers chart
              has its priorities backwards. */}
          {data.allergies.length > 0 ? (
            <section className="rounded-lg border-2 border-red-300 bg-red-50 p-4">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-red-900">
                {t('preShift.allergies')}
              </h2>
              <ul className="mt-2 space-y-1">
                {data.allergies.map((row) => (
                  <li key={row.booking_ref} className="text-sm">
                    <span className="font-mono" data-numeric>{row.clock}</span>
                    {' · '}
                    <span className="font-medium">{row.guest}</span>
                    {' — '}
                    <span className="font-semibold text-red-800">{row.allergies}</span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {[
            { key: 'preShift.vips', rows: data.vips, render: (r: PreShift['vips'][number]) => `${r.guest} · ${r.total_visits}` },
            { key: 'preShift.occasions', rows: data.occasions, render: (r: PreShift['occasions'][number]) => `${r.guest} — ${r.occasion}` },
            { key: 'preShift.largeParties', rows: data.large_parties, render: (r: PreShift['large_parties'][number]) => `${r.guest} · ${r.party_size}` },
            { key: 'preShift.requests', rows: data.special_requests, render: (r: PreShift['special_requests'][number]) => r.request },
          ].map((block) =>
            block.rows.length > 0 ? (
              <section key={block.key} className="rounded-lg border border-slate-200 bg-white p-4">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                  {t(block.key)}
                </h2>
                <ul className="mt-2 space-y-1">
                  {block.rows.map((row) => (
                    <li key={row.booking_ref} className="text-sm">
                      <span className="font-mono text-slate-500" data-numeric>
                        {row.clock}
                      </span>
                      {' · '}
                      {block.render(row as never)}
                    </li>
                  ))}
                </ul>
              </section>
            ) : null,
          )}

          <section className="rounded-lg border border-slate-200 bg-white p-4">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              {t('preShift.byInterval')}
            </h2>
            <ul className="mt-3 flex items-end gap-1">
              {data.covers_by_interval.map((row) => (
                <li key={row.clock} className="flex flex-1 flex-col items-center gap-1">
                  {/* A bar chart in flex, so it mirrors with the page rather
                      than needing its own direction handling. */}
                  <span
                    className="w-full rounded-t bg-brand-teal"
                    style={{ height: `${(row.covers / maxCovers) * 80 + 4}px` }}
                    aria-hidden
                  />
                  <span className="font-mono text-[10px] text-slate-500" data-numeric>
                    {row.clock}
                  </span>
                </li>
              ))}
            </ul>
          </section>

          <section className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th scope="col" className="px-3 py-2 text-start">{t('common.time')}</th>
                  <th scope="col" className="px-3 py-2 text-start">{t('common.guest')}</th>
                  <th scope="col" className="px-3 py-2 text-start">{t('common.partySize')}</th>
                  <th scope="col" className="px-3 py-2 text-start">{t('guests.visits')}</th>
                </tr>
              </thead>
              <tbody>
                {data.bookings.map((row) => (
                  <tr key={row.booking_ref} className="border-t border-slate-100">
                    <td className="px-3 py-1.5 font-mono" data-numeric>{row.clock}</td>
                    <td className="px-3 py-1.5">{row.guest}</td>
                    <td className="px-3 py-1.5 font-mono" data-numeric>{row.party_size}</td>
                    <td className="px-3 py-1.5 font-mono" data-numeric>{row.visits}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data.bookings.length === 0 ? (
              <p className="p-6 text-center text-sm text-slate-500">{t('preShift.empty')}</p>
            ) : null}
          </section>
        </>
      ) : null}
    </div>
  );
}
